// The Courses table's state for the browser tab: the table publishes its state and this page keeps it in
// sessionStorage, so returning to the page restores the query, page and View. Nothing goes in the URL.

export const TABLE_ID = 'admin-courses'

export const storageKey = user => `me-table:${TABLE_ID}:${user}`

const isPlainObject = value =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const storage = () => {
  try {
    return window.sessionStorage || null
  } catch (e) {
    return null
  }
}

// Keeps only the published fields, each with the type the table publishes; anything else is dropped.
export const sanitize = input => {
  if (!isPlainObject(input) || input.tableId !== TABLE_ID) return null
  const out = {}
  if (Array.isArray(input.columns))
    out.columns = input.columns.filter(key => typeof key === 'string')
  if (Array.isArray(input.hidden))
    out.hidden = input.hidden.filter(key => typeof key === 'string')
  if (isPlainObject(input.widths)) {
    const widths = {}
    Object.entries(input.widths).forEach(([key, width]) => {
      if (typeof width === 'number' && Number.isFinite(width))
        widths[key] = width
    })
    out.widths = widths
  }
  if (input.density === 'comfortable' || input.density === 'compact')
    out.density = input.density
  if (typeof input.search === 'string') out.search = input.search
  if (isPlainObject(input.filters)) out.filters = input.filters
  if (
    isPlainObject(input.sort) &&
    typeof input.sort.key === 'string' &&
    (input.sort.direction === 'ascending' ||
      input.sort.direction === 'descending')
  )
    out.sort = { key: input.sort.key, direction: input.sort.direction }
  if (Number.isInteger(input.page) && input.page > 0) out.page = input.page
  if (Number.isInteger(input.pageSize) && input.pageSize > 0)
    out.pageSize = input.pageSize
  return Object.keys(out).length ? out : null
}

/**
 * The saved state for this user, or null when there is none or it cannot be read.
 * @param {string} user
 * @returns {?object} published-shape state for the table's controlledState
 */
export const readState = user => {
  const store = storage()
  if (!user || !store) return null
  try {
    const raw = store.getItem(storageKey(user))
    return raw ? sanitize(JSON.parse(raw)) : null
  } catch (e) {
    return null
  }
}

/**
 * Saves a published state. Private browsing and a full quota fail quietly.
 * @param {string} user
 * @param {object} state
 */
export const writeState = (user, state) => {
  const store = storage()
  if (!user || !store || !isPlainObject(state)) return
  try {
    store.setItem(
      storageKey(user),
      JSON.stringify({ ...state, tableId: TABLE_ID })
    )
  } catch (e) {
    // nothing to restore next time
  }
}
